/**
 * Budget vs. actual, and the forecast scenarios beside it (KAN-56 / KAN-57 /
 * KAN-93 / KAN-94).
 *
 * Two things shape this screen and both are about not misleading:
 *
 * **A line with no budget says so.** It never reads 100% over, and it never
 * reads 0% variance. A fabricated variance is worse than an admitted gap
 * because somebody acts on it.
 *
 * **Favourable is not the same as under.** Over budget on an expense is bad and
 * over budget on revenue is good, so the colour comes from the account's
 * classification rather than from the sign of the number — and an account with
 * no natural direction gets no colour rather than a guess.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ACCOUNTING_METHODS,
  BUDGET_VIEWS,
  BUDGET_VIEW_LABELS,
  SCENARIO_STATUS_LABELS,
  VARIANCE_STATE_LABELS,
  formatBps,
  type AccountingMethod,
  type BudgetComparison,
  type BudgetView,
  type ForecastScenario,
  type ScenarioComparison,
  type VarianceState,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatDate, formatMoney } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

const STATE_TONES: Record<VarianceState, Tone> = {
  on_budget: 'neutral',
  favourable: 'positive',
  unfavourable: 'critical',
  /** Neither good nor bad — an admitted gap, and it must not look like either. */
  no_budget: 'accent',
  no_actual: 'neutral',
  undirected: 'neutral',
}

export function Budget() {
  const { may } = useSession()
  const [view, setView] = useState<BudgetView>('monthly')
  const [method, setMethod] = useState<AccountingMethod>('Accrual')

  const comparison = useQuery({
    queryKey: ['budgets', view, method],
    queryFn: () =>
      apiFetch<BudgetComparison>(`/budgets?view=${view}&method=${method}`),
  })

  return (
    <>
      <PageHeader
        title="Budget vs. actual"
        description="The firm’s budget beside what QuickBooks says was actually earned and spent. Budgets are maintained here; actuals are read from QuickBooks at the moment you open this page."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-md border border-ink-300 bg-white p-0.5">
              {BUDGET_VIEWS.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={option === view ? 'primary' : 'ghost'}
                  onClick={() => setView(option)}
                >
                  {BUDGET_VIEW_LABELS[option]}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-md border border-ink-300 bg-white p-0.5">
              {ACCOUNTING_METHODS.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={option === method ? 'primary' : 'ghost'}
                  onClick={() => setMethod(option)}
                >
                  {option}
                </Button>
              ))}
            </div>
          </div>
        }
      />

      {comparison.isPending && <p className="text-sm text-ink-600">Loading…</p>}

      {comparison.isError && (
        <Alert tone="error" title="The comparison could not be loaded">
          {(comparison.error as Error).message}
        </Alert>
      )}

      {comparison.data && (
        <div className="space-y-6">
          <Comparison comparison={comparison.data} />
          <Scenarios canManage={may('budgets.manage')} />
        </div>
      )}
    </>
  )
}

function Comparison({ comparison }: { comparison: BudgetComparison }) {
  return (
    <>
      {!comparison.actualsAvailable && (
        <Alert tone="warning" title="Actuals could not be read from QuickBooks">
          <p>{comparison.unavailableReason}</p>
          <p className="mt-2">
            The budgets below are the firm’s own and are unaffected. Every actual is shown as
            unknown rather than as a zero, because a zero would report the firm as having earned
            and spent nothing and every line as a total underspend.
          </p>
        </Alert>
      )}

      <StatRow className="lg:grid-cols-3">
        <Stat
          label="Revenue budget"
          value={formatMoney(comparison.totals.revenueBudgetedMinor)}
          hint={
            comparison.totals.revenueActualMinor === null
              ? 'Actual unknown — QuickBooks could not be read.'
              : `Actual ${formatMoney(comparison.totals.revenueActualMinor)}.`
          }
        />
        <Stat
          label="Expense budget"
          value={formatMoney(comparison.totals.expenseBudgetedMinor)}
          hint={
            comparison.totals.expenseActualMinor === null
              ? 'Actual unknown — QuickBooks could not be read.'
              : `Actual ${formatMoney(comparison.totals.expenseActualMinor)}.`
          }
        />
        <Stat
          label="Period"
          value={`${formatDate(comparison.from)} – ${formatDate(comparison.to)}`}
          tone="muted"
          hint={`${comparison.accountingMethod} basis. Cash and accrual give materially different answers, so the basis travels with the figures.`}
        />
      </StatRow>

      {comparison.proration && (
        <Alert tone="info" title="Some budgets were apportioned">
          {comparison.proration}
        </Alert>
      )}

      {comparison.unmatchedCategories.length > 0 && (
        <Alert tone="info" title="Budget lines with no QuickBooks account">
          <p>
            {comparison.unmatchedCategories.join(', ')} — these are budgeted and reported, but
            nothing in QuickBooks is matched to them, so no actual can be compared. They are never
            attached to the nearest account by name.
          </p>
        </Alert>
      )}

      <Card>
        <CardHeader
          title="By account"
          description="Largest first, because a budget report is read looking for what moved — an alphabetical list buries a large overspend under “Bank charges”."
        />
        <TableWrap>
          <thead>
            <tr>
              <Th>Account</Th>
              <Th className="text-right">Budget</Th>
              <Th className="text-right">Actual</Th>
              <Th className="text-right">Variance</Th>
              <Th className="text-right">%</Th>
              <Th>Reading</Th>
            </tr>
          </thead>
          <tbody>
            {comparison.lines.length === 0 && (
              <EmptyRow colSpan={6}>
                No budget lines cover this period. Import a budget to compare against.
              </EmptyRow>
            )}
            {comparison.lines.map((line) => (
              <tr key={line.accountId ?? line.category}>
                <Td>
                  {line.category}
                  {line.accountNumber && (
                    <span className="ml-2 text-xs text-ink-500">{line.accountNumber}</span>
                  )}
                </Td>
                <Td className="text-right tabular-nums">
                  {line.budgetedMinor === null ? '—' : formatMoney(line.budgetedMinor)}
                </Td>
                <Td className="text-right tabular-nums">
                  {line.actualMinor === null ? '—' : formatMoney(line.actualMinor)}
                </Td>
                <Td className="text-right tabular-nums">
                  {line.varianceMinor === null ? '—' : formatMoney(line.varianceMinor)}
                </Td>
                <Td className="text-right tabular-nums">
                  {line.varianceBps === null ? '—' : formatBps(line.varianceBps)}
                </Td>
                <Td>
                  <Badge tone={STATE_TONES[line.state]}>{VARIANCE_STATE_LABELS[line.state]}</Badge>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Card>
    </>
  )
}

/* ------------------------------------------------------------------------ *
 * Scenarios
 * ------------------------------------------------------------------------ */

function Scenarios({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string[]>([])
  const [name, setName] = useState('')

  const scenarios = useQuery({
    queryKey: ['budgets', 'scenarios'],
    queryFn: () => apiFetch<ForecastScenario[]>('/budgets/scenarios'),
  })

  const comparison = useQuery({
    queryKey: ['budgets', 'scenarios', 'compare', selected],
    queryFn: () =>
      apiFetch<ScenarioComparison>(`/budgets/scenarios/compare?ids=${selected.join(',')}`),
    enabled: selected.length > 0,
  })

  const create = useMutation({
    mutationFn: (cloneOf: string | null) =>
      apiFetch('/budgets/scenarios', {
        method: 'POST',
        body: JSON.stringify({ name, cloneOf: cloneOf ?? undefined }),
      }),
    onSuccess: async () => {
      setName('')
      await queryClient.invalidateQueries({ queryKey: ['budgets', 'scenarios'] })
    },
  })

  const lock = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/budgets/scenarios/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ lock: true, status: 'active' }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['budgets', 'scenarios'] }),
  })

  /** Two or three side by side; more does not fit on a screen or in a decision. */
  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id].slice(-3)
    )
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Forecast scenarios"
          description="Named versions of the plan. Cloning is the usual way to start one — editing a clone provably never touches its parent, which is the whole point of having versions."
          actions={
            canManage && (
              <div className="flex items-center gap-2">
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Two associates from July"
                  className="rounded-md border border-ink-300 px-3 py-1.5 text-sm"
                />
                <Button
                  size="sm"
                  variant="accent"
                  disabled={!name.trim() || create.isPending}
                  onClick={() => create.mutate(null)}
                >
                  New from budget
                </Button>
              </div>
            )
          }
        />

        <TableWrap>
          <thead>
            <tr>
              <Th>Compare</Th>
              <Th>Name</Th>
              <Th>Cloned from</Th>
              <Th>Status</Th>
              <Th>Adjustments</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {scenarios.isPending && <EmptyRow colSpan={6}>Loading…</EmptyRow>}
            {scenarios.data?.length === 0 && (
              <EmptyRow colSpan={6}>
                No scenarios yet. Start one from the budget, then clone it to model an alternative.
              </EmptyRow>
            )}
            {scenarios.data?.map((scenario) => (
              <tr key={scenario.id}>
                <Td>
                  <input
                    type="checkbox"
                    checked={selected.includes(scenario.id)}
                    onChange={() => toggle(scenario.id)}
                    aria-label={`Compare ${scenario.name}`}
                  />
                </Td>
                <Td>
                  <span className="font-medium text-brand-900">{scenario.name}</span>
                  {scenario.description && (
                    <p className="mt-0.5 text-xs text-ink-500">{scenario.description}</p>
                  )}
                </Td>
                <Td>{scenario.baseScenarioName ?? 'The budget'}</Td>
                <Td>
                  <Badge tone={scenario.isLocked ? 'brand' : 'neutral'}>
                    {scenario.isLocked ? 'Locked' : SCENARIO_STATUS_LABELS[scenario.status]}
                  </Badge>
                </Td>
                <Td>
                  {scenario.adjustments.length === 0 ? (
                    <span className="text-ink-500">None</span>
                  ) : (
                    <ul className="space-y-0.5 text-xs text-ink-700">
                      {scenario.adjustments.map((adjustment) => (
                        <li key={adjustment.id}>
                          {adjustment.label}
                          {adjustment.kind === 'rate' && adjustment.rateBps !== null && (
                            <span className="ml-1 text-ink-500">
                              ({formatBps(adjustment.rateBps)})
                            </span>
                          )}
                          {adjustment.kind === 'amount' && adjustment.amountMinor !== null && (
                            <span className="ml-1 text-ink-500">
                              ({formatMoney(adjustment.amountMinor)})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Td>
                <Td>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setName(`${scenario.name} (copy)`)
                          create.mutate(scenario.id)
                        }}
                      >
                        Clone
                      </Button>
                      {!scenario.isLocked && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => lock.mutate(scenario.id)}
                          title="Locking is one way. To change it afterwards, clone it — that leaves both versions readable."
                        >
                          Lock
                        </Button>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Card>

      {selected.length > 0 && comparison.data && (
        <SideBySide comparison={comparison.data} />
      )}
    </>
  )
}

function SideBySide({ comparison }: { comparison: ScenarioComparison }) {
  const rows = useMemo(() => {
    return comparison.categories.map((category) => ({
      category,
      cells: comparison.scenarios.map(
        (entry) => entry.lines.find((line) => line.category === category) ?? null
      ),
    }))
  }, [comparison])

  return (
    <Card>
      <CardHeader
        title="Side by side"
        description={`${comparison.scenarios.map((entry) => entry.scenario.name).join(' · ')} — over ${formatDate(comparison.from)} to ${formatDate(comparison.to)}, ${comparison.accountingMethod} basis.`}
      />
      <TableWrap>
        <thead>
          <tr>
            <Th>Category</Th>
            {comparison.scenarios.map((entry) => (
              <Th key={entry.scenario.id} className="text-right">
                {entry.scenario.name}
              </Th>
            ))}
            <Th className="text-right">Actual</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.category}>
              <Td>{row.category}</Td>
              {row.cells.map((cell, index) => (
                <Td
                  key={comparison.scenarios[index]?.scenario.id ?? index}
                  className="text-right tabular-nums"
                >
                  {cell === null ? '—' : formatMoney(cell.scenarioMinor)}
                  {cell && cell.applied.length > 0 && (
                    <p className="mt-0.5 text-xs font-normal text-ink-500">
                      {cell.applied
                        .map((entry) => `${entry.label} ${formatMoney(entry.deltaMinor)}`)
                        .join(' · ')}
                    </p>
                  )}
                </Td>
              ))}
              <Td className="text-right tabular-nums">
                {formatMoney(row.cells[0]?.actualMinor ?? null)}
              </Td>
            </tr>
          ))}
          <tr className="bg-ink-50 font-semibold">
            <Td>Total</Td>
            {comparison.scenarios.map((entry) => (
              <Td key={entry.scenario.id} className="text-right tabular-nums">
                {formatMoney(entry.totalMinor)}
              </Td>
            ))}
            <Td className="text-right tabular-nums">
              {comparison.scenarios[0]?.actualTotalMinor === null
                ? '—'
                : formatMoney(comparison.scenarios[0]?.actualTotalMinor ?? null)}
            </Td>
          </tr>
        </tbody>
      </TableWrap>

      {!comparison.actualsAvailable && (
        <CardBody>
          <p className="text-sm text-ink-600">
            Actuals are unavailable: {comparison.unavailableReason} Each scenario is still
            comparable against the others — only the column measuring them against reality is
            missing.
          </p>
        </CardBody>
      )}
    </Card>
  )
}
